import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, TreePine } from "lucide-react";
import { Link } from "react-router-dom";
import { InstallButton } from "@/components/InstallButton";
import { AREAS } from "@/data/areas";
import { TRIPS } from "@/data/trips";
import { MONTH_SLUGS } from "@/data/months";

const LINKS = [
  { type: "route", label: "Areas", to: "/areas", children: [...AREAS.map((a) => ({ label: a.name, to: `/areas/${a.id}` }))] },
  { type: "route", label: "3 days — for first time visitors", to: "/copenhagen-itinerary", children: [{ label: "Like a local — for returning visitors", to: "/copenhagen-like-a-local" }, { label: "With kids — the family plan", to: "/copenhagen-with-kids-itinerary" }, { label: "Winter (Tivoli closed)", to: "/copenhagen-in-winter" }, { label: "EDT", to: "/edt" }, { label: "Layover guide", to: "/layover" }] },
  { type: "route", label: "What to eat", to: "/danish-food", children: [{ label: "Bakeries", to: "/bakeries" }, { label: "The bakery crawl", to: "/bakery-crawl" }, { label: "Smørrebrød", to: "/smorrebrod" }, { label: "Food markets", to: "/copenhagen-food-markets" }] },
  { type: "route", label: "Transport", to: "/transport", children: [{ label: "Biking in Copenhagen", to: "/biking" }] },
  { type: "route", label: "Where to stay", to: "/stay" },
  { type: "route", label: "Month by month", to: `/copenhagen-in/${MONTH_SLUGS[new Date().getMonth()]}`, children: [{ label: "Rainy day guide", to: "/rainy-day" }] },
  { type: "route", label: "Christmas", to: "/christmas", tree: true, children: [
    { label: "The Christmas walk", to: "/christmas-walk" },
    { label: "Markets", to: "/christmas#markets" },
    { label: "Gløgg guide", to: "/glogg" },
    { label: "Christmas food places", to: "/christmas-food" },
    { label: "Traditions & events", to: "/christmas#traditions" },
  ] },
  { type: "route", label: "On a budget", to: "/budget", children: [{ label: "Free things to do", to: "/budget#free-things" }] },
  { type: "route", label: "Swim the harbour", to: "/harbour-swimming" },
  { type: "route", label: "With kids", to: "/copenhagen-with-kids" },
  { type: "route", label: "Shopping streets", to: "/copenhagen-shopping-streets" },
  { type: "route", label: "Photo-worthy streets", to: "/photo-worthy-streets" },
  { type: "route", label: "Day trips", to: "/trips", children: [...TRIPS.map((t) => ({ label: t.name, to: `/trips/${t.id}` })), { label: "Further away — Odense, Aarhus & more", to: "/beyond-copenhagen" }] },
  { type: "route", label: "H.C. Andersen", to: "/hc-andersen" },
  { type: "scroll", label: "FAQ", to: "#faq" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const renderLink = (l, mobile) => {
    const base = mobile
      ? "font-body font-700 py-2 border-b border-zinc-200/10"
      : "font-body font-700 text-[13px] px-2.5 py-2 rounded-md whitespace-nowrap hover:bg-black hover:text-white transition-colors";

    // parent with a dropdown of sub-links
    if (l.children) {
      const wide = l.children.length > 6;
      const parentTrigger = (extra) =>
        l.type === "route" ? (
          <Link
            to={l.to}
            data-testid={`nav-link-${l.to.replace("/", "")}-page`}
            onClick={() => setOpen(false)}
            className={extra}
          >
            {l.label}
            {l.tree && <TreePine size={14} className="inline-block ml-1 -mt-0.5 text-[#0E7C5A]" />}
            {mobile && <span aria-hidden className="ml-1.5 font-900 text-[#DF3226]">→</span>}
            {!mobile && <ChevronDown size={13} className="opacity-60" />}
          </Link>
        ) : (
          <a
            href={l.to}
            data-scroll={l.to}
            data-testid={`nav-link-${l.to.slice(1)}`}
            onClick={() => setOpen(false)}
            className={extra}
          >
            {l.label}
            {l.tree && <TreePine size={14} className="inline-block ml-1 -mt-0.5 text-[#0E7C5A]" />}
            {mobile && <span aria-hidden className="ml-1.5 font-900 text-[#DF3226]">→</span>}
            {!mobile && <ChevronDown size={13} className="opacity-60" />}
          </a>
        );

      if (!mobile) {
        return (
          <div key={l.to} className="relative group">
            {parentTrigger(`${base} inline-flex items-center gap-1`)}
            <div data-nosnippet className="absolute left-0 top-full pt-2 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-150 z-50">
              <div className={`bg-white border border-zinc-200 shadow-brut rounded-lg py-1.5 ${wide ? "grid grid-cols-2 min-w-[320px]" : "min-w-[170px]"}`}>
                {l.children.map((c) => (
                  <Link
                    key={c.to}
                    to={c.to}
                    data-testid={`nav-sublink-${c.to.replace(/\//g, "-").replace(/^-/, "")}`}
                    onClick={() => setOpen(false)}
                    className="block px-4 py-2 font-body font-700 text-[13px] hover:bg-black hover:text-white transition-colors whitespace-nowrap"
                  >
                    {c.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        );
      }
      return (
        <div key={l.to} className="flex flex-col">
          <div className="flex items-center justify-between border-b border-zinc-200/10">
            {parentTrigger("font-body font-700 py-2 flex-1")}
            <button
              type="button"
              aria-label={`Toggle ${l.label}`}
              data-testid={`nav-accordion-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              onClick={() => setExpanded(expanded === l.to ? null : l.to)}
              className="p-2 -mr-2"
            >
              <ChevronDown size={18} className={`transition-transform ${expanded === l.to ? "rotate-180" : ""}`} />
            </button>
          </div>
          {expanded === l.to && (
            <div className={wide ? "grid grid-cols-2 py-1" : "flex flex-col py-1"}>
              {l.children.map((c) => (
                <Link
                  key={c.to}
                  to={c.to}
                  data-nosnippet
                  data-testid={`nav-sublink-${c.to.replace(/\//g, "-").replace(/^-/, "")}-mobile`}
                  onClick={() => setOpen(false)}
                  className="font-body font-600 text-sm py-2 pl-5 border-b border-zinc-200/10 text-black/70"
                >
                  ↳ {c.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (l.type === "route") {
      return (
        <Link key={l.to} to={l.to} data-testid={`nav-link-${l.to.replace("/", "")}-page`} onClick={() => setOpen(false)} className={base}>
          {l.label}
        </Link>
      );
    }
    return (
      <a
        key={l.to}
        href={l.to}
        data-scroll={l.to}
        data-testid={`nav-link-${l.to.slice(1)}`}
        onClick={() => setOpen(false)}
        className={base}
      >
        {l.label}
      </a>
    );
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 border-b border-zinc-200 ${
        scrolled ? "bg-[#F9F8F6]/90 backdrop-blur-xl backdrop-saturate-150" : "bg-[#F9F8F6]"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="group shrink-0 leading-none">
          <span className="block font-display font-900 text-lg md:text-xl tracking-tighter leading-none">
            CPH Highlights
          </span>
          <span className="flex items-center gap-2 mt-2">
            <span className="h-[2px] w-4 bg-[#DF3226] shrink-0 group-hover:w-7 transition-[width] duration-300 ease-out" />
            <span className="font-body font-800 text-[9px] md:text-[10px] uppercase tracking-[0.28em] text-black/50 leading-none">
              The Modern Guide
            </span>
            <span className="h-3 w-px bg-black/20 shrink-0" />
            <span className="font-editorial italic text-[13px] md:text-[14px] text-black/50 leading-none">
              <span className="text-[17px] md:text-[19px] leading-none">M</span>ade by locals
            </span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-0.5">
          {LINKS.map((l) => renderLink(l, false))}
          <InstallButton />
          <Link
            to="/planner"
            data-testid="nav-plan-cta"
            className="ml-2 font-display font-800 text-sm whitespace-nowrap bg-[#DF3226] text-white px-6 py-2.5 rounded-full shadow-lg shadow-[#DF3226]/20 hover:-translate-y-0.5 hover:shadow-xl transition-all"
          >
            Plan my trip →
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="xl:hidden w-11 h-11 flex items-center justify-center border border-zinc-200 bg-white"
          aria-label="Menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            className="xl:hidden overflow-hidden bg-[#F9F8F6] border-t border-dotted border-zinc-300"
          >
            <div
              data-lenis-prevent
              className="px-5 py-4 flex flex-col gap-1 max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain"
            >
              <Link
                to="/planner"
                onClick={() => setOpen(false)}
                data-testid="nav-plan-cta-mobile"
                className="mb-3 text-center font-display font-800 bg-[#DF3226] text-white px-5 py-3 rounded-full"
              >
                Plan my trip →
              </Link>
              {LINKS.map((l) => renderLink(l, true))}
              <InstallButton mobile onNavigate={() => setOpen(false)} />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
